import React from 'react';
import { Card, Table, Button, Modal, Input, List, message, Tag, Tabs, Space, Drawer } from 'antd';
import { ClockCircleOutlined, RobotOutlined } from '@ant-design/icons';

import { supportAPI } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

const SupportAdmin: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [tickets, setTickets] = React.useState<any[]>([]);
  const [archived, setArchived] = React.useState<any[]>([]);
  const [tab, setTab] = React.useState<'active' | 'archive'>('active');
  const [selected, setSelected] = React.useState<any | null>(null);
  const [reply, setReply] = React.useState('');

  // AI Assistant state
  const [aiDrawerVisible, setAiDrawerVisible] = React.useState(false);
  const [aiSuggestion, setAiSuggestion] = React.useState('');
  const [aiLoading, setAiLoading] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [active, arch] = await Promise.all([
        supportAPI.getTickets({ archived: false }),
        supportAPI.getTickets({ archived: true }),
      ]);
      setTickets(active);
      setArchived(arch);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить обращения');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { 
    load(); 
  }, []);

  // Auto-refresh every 30s (только если модал закрыт)
  React.useEffect(() => {
    if (selected) return; // не обновляем, если открыт модал ответа
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
  }, [selected]);

  const openTicket = async (t: any) => {
    setSelected(t);
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    
    // Проверка на грубость/нецензурность
    const moderation = checkMessageModeration(reply);
    if (!moderation.allowed) {
      message.error(`⚠️ Сообщение содержит недопустимый контент: ${moderation.reason}`);
      return;
    }
    
    try {
      await supportAPI.replyTicket(selected.id, reply);
      message.success('Ответ отправлен');
      setReply('');
      // Обновляем список тикетов
      await load();
      // Обновляем выбранный тикет
      const updatedTickets = await supportAPI.getTickets({ archived: false });
      const updatedTicket = updatedTickets.find((t: any) => t.id === selected.id);
      if (updatedTicket) {
        setSelected(updatedTicket);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Ошибка отправки');
    }
  };

  // Система модерации сообщений
  const checkMessageModeration = (text: string): { allowed: boolean; reason?: string; cleaned?: string } => {
    const profanityList = [
      'хуй', 'блядь', 'сука', 'пизда', 'ебать', 'ебись', 'пиздец', 'мудак', 'дебил', 'козёл',
      'урод', 'дрочи', 'соси', 'пиздуй', 'нахуй', 'похуй', 'ебучий', 'блять', 'чмо',
      'fuck', 'shit', 'damn', 'ass', 'bitch', 'dick', 'crap', 'piss', 'motherfucker'
    ];

    const rudnessPatterns = [
      /^(fuck|go fuck|fuck you|fuck off|get lost|stupid|idiot|moron).*/i,
      /^(иди нахуй|пиздуй|ебись|заебал|засрал).*/i,
      /ты идиот|ты урод|ты тупой|ты дебил/i,
      /не буду|не хочу выполнять|не интересует/i // может быть грубым тоном
    ];

    const lowerText = text.toLowerCase();
    
    // Проверка нецензурных слов
    for (const word of profanityList) {
      if (lowerText.includes(word)) {
        return { 
          allowed: false, 
          reason: 'Используются недопустимые слова' 
        };
      }
    }

    // Проверка грубых паттернов
    for (const pattern of rudnessPatterns) {
      if (pattern.test(text)) {
        return { 
          allowed: false, 
          reason: 'Грубый или оскорбительный тон' 
        };
      }
    }

    // Проверка БОЛЬШОГО ТЕКСТА (может быть агрессивной)
    const capsCount = (text.match(/[A-ZА-Я]/g) || []).length;
    if (capsCount / text.length > 0.5) {
      return { 
        allowed: false, 
        reason: 'Слишком много ЗАГЛАВНЫХ букв (признак крика)' 
      };
    }

    return { allowed: true };
  };

  const generateAIResponse = async () => {
    if (!selected) return;
    setAiLoading(true);
    try {
      const subject = selected.subject.toLowerCase();
      const messages = selected.messages || [];
      const lastMessage = messages[messages.length - 1]?.message || '';

      // Простая система правил
      let suggestion = '';
      if (subject.includes('не пришел') || lastMessage.includes('где')) {
        suggestion = 'Благодарим за ваше терпение. Ваш заказ обрабатывается. Проверьте статус в личном кабинете. Свяжемся с вами в ближайшее время.';
      } else if (subject.includes('качество') || lastMessage.includes('испорчено')) {
        suggestion = 'Нам очень жаль. Пожалуйста, отправьте фото проблемы. Мы предложим решение в течение 24 часов.';
      } else if (subject.includes('платеж') || subject.includes('деньги')) {
        suggestion = 'Платеж не прошел. Попробуйте еще раз или используйте другой способ оплаты. Если не поможет - свяжитесь с вашим банком.';
      } else {
        suggestion = 'Спасибо за обращение. Наша команда разберется с вашей проблемой и свяжется с вами в течение 24 часов.';
      }

      setAiSuggestion(suggestion);
      setAiDrawerVisible(true);
      message.success('ИИ сгенерировал предложение');
    } catch (e: any) {
      message.error('Ошибка генерации ответа');
    } finally {
      setAiLoading(false);
    }
  };

  const closeTicket = async () => {
    if (!selected) return;
    try {
      console.log('🔵 Closing ticket:', selected.id);
      console.log('📍 API endpoint: PUT /api/support/tickets/' + selected.id + '/close');
      console.log('🔑 Token exists:', !!localStorage.getItem('token'));
      
      const result = await supportAPI.closeTicket(selected.id);
      
      console.log('✅ Close result:', result);
      message.success('Обращение закрыто');
      setSelected(null);
      await load();
    } catch (e: any) {
      console.error('❌ Close ticket error:', e);
      console.error('📋 Error details:', {
        message: e?.message,
        response: e?.response?.data,
        status: e?.response?.status,
        config: e?.config?.url
      });
      message.error(e?.response?.data?.error || e.message || 'Ошибка закрытия: Network Error');
    }
  };

  const Timer: React.FC<{ ticket: any }> = ({ ticket }) => {
    const [elapsed, setElapsed] = React.useState(0);
    const isClosed = ticket.status === 'closed' || ticket.status === 'answered';
    
    React.useEffect(() => {
      if (isClosed) return;
      const id = setInterval(() => {
        // Считаем от lastResponseAt, если есть, иначе от createdAt
        const baseTime = ticket.lastResponseAt ? new Date(ticket.lastResponseAt) : new Date(ticket.createdAt);
        const diff = Math.floor((Date.now() - baseTime.getTime()) / 1000);
        setElapsed(diff);
      }, 1000);
      return () => clearInterval(id);
    }, [ticket, isClosed]);
    
    // Не показываем таймер для закрытых или отвеченных обращений
    if (isClosed) {
      return <Tag color="blue">{ticket.status === 'closed' ? 'Закрыто' : 'Отвечено'}</Tag>;
    }
    
    const remaining = 300 - elapsed; // 5 min = 300s
    const urgent = remaining <= 0;
    const mins = Math.floor(Math.abs(remaining) / 60);
    const secs = Math.abs(remaining) % 60;
    return urgent ? 
      <Tag color="red">Просрочено {mins}:{secs < 10 ? '0' : ''}{secs}</Tag> : 
      <Tag color="green">Осталось {mins}:{secs < 10 ? '0' : ''}{secs}</Tag>;
  };

  const columns = [
    { 
      title: 'ID', 
      dataIndex: 'id',
      width: 80
    },
    { 
      title: 'Тема', 
      dataIndex: 'subject',
      ellipsis: true
    },
    { 
      title: 'Пользователь',
      render: (_: any, rec: any) => {
        if (rec.user?.username) return rec.user.username;
        const contact = rec.guestEmail || rec.guestPhone || '-';
        const name = rec.guestName || 'Гость';
        return `${name} (${contact})`;
      }
    },
    { 
      title: 'Статус', 
      dataIndex: 'status',
      render: (status: string) => {
        const colors: any = {
          open: 'red',
          answered: 'blue',
          closed: 'green'
        };
        const labels: any = {
          open: 'Открыт',
          answered: 'Отвечено',
          closed: 'Закрыт'
        };
        return <Tag color={colors[status]}>{labels[status] || status}</Tag>;
      }
    },
    { 
      title: 'Приоритет',
      dataIndex: 'priority',
      render: (priority: string) => {
        const colors: any = {
          high: 'red',
          medium: 'orange',
          low: 'green'
        };
        const labels: any = {
          high: 'Высокий',
          medium: 'Средний',
          low: 'Низкий'
        };
        return <Tag color={colors[priority]}>{labels[priority] || priority}</Tag>;
      }
    },
    { 
      title: 'Время реакции', 
      render: (_: any, rec: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ClockCircleOutlined />
          <Timer ticket={rec} />
        </div>
      )
    },
    { 
      title: 'Ответственный', 
      width: 150,
      render: (_: any, rec: any) => {
        const messages = rec.messages || [];
        if (messages.length === 0) return '-';
        
        // Проверяем последний ответ от поддержки
        const lastSupportResponse = messages.filter((m: any) => !m.isFromUser).pop();
        
        if (!lastSupportResponse) return '-';
        
        // Для теста показываем ИИ если нет пользователя в ответе
        // В реальности это должно быть поле isAIGenerated
        const isAI = !lastSupportResponse.user;
        
        return (
          <div style={{ fontSize: '13px' }}>
            {isAI ? (
              <Tag icon={<RobotOutlined />} color="blue">
                ИИ Ассистент
              </Tag>
            ) : (
              <Tag color="green">
                {lastSupportResponse.user?.username || 'Оператор'}
              </Tag>
            )}
          </div>
        )
      }
    },
    { 
      title: 'Действия', 
      render: (_: any, rec: any) => (
        <Button type="primary" size="small" onClick={() => openTicket(rec)}>
          Открыть
        </Button>
      )
    },
  ];

  return (
    <>
      <Card title="Кабинет поддержки (обращения)" loading={loading}>
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as any)}
          items={[
            { 
              key: 'active', 
              label: `Активные (${tickets.length})`, 
              children: (
                <Table 
                  rowKey="id" 
                  columns={columns as any} 
                  dataSource={tickets} 
                  pagination={{ pageSize: 10 }} 
                  scroll={isMobile ? { x: 'max-content' } : undefined}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            { 
              key: 'archive', 
              label: `Архив (${archived.length})`, 
              children: (
                <Table 
                  rowKey="id" 
                  columns={columns as any} 
                  dataSource={archived} 
                  pagination={{ pageSize: 10 }} 
                  scroll={isMobile ? { x: 'max-content' } : undefined}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
          ]}
        />

        <Modal
          title={selected ? `Обращение #${selected.id}: ${selected.subject}` : ''}
          open={!!selected}
          onCancel={() => setSelected(null)}
          footer={null}
          width={isMobile ? '100%' : 700}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
        >
          {selected && (
            <>
              <List
                dataSource={selected.messages || []}
                renderItem={(m: any) => (
                  <List.Item>
                    <div style={{ width: '100%' }}>
                      <div style={{ fontWeight: !m.isFromUser ? 600 : 400 }}>
                        {!m.isFromUser
                          ? 'Служба поддержки'
                          : `Пользователь: ${m.user?.username || selected?.guestName || 'Гость'}`}
                      </div>
                      <div>{m.message}</div>
                      <div style={{ color: '#999', fontSize: 12 }}>
                        {new Date(m.createdAt).toLocaleString('ru')}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
              <div style={{ marginTop: 12 }}>
                <Input.TextArea 
                  rows={3} 
                  value={reply} 
                  onChange={(e) => setReply(e.target.value)} 
                  placeholder="Ваш ответ" 
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <Button type="primary" onClick={sendReply}>
                    Ответить
                  </Button>
                  <Button icon={<RobotOutlined />} onClick={generateAIResponse} loading={aiLoading}>
                    ИИ помощник
                  </Button>
                  {selected.status !== 'closed' && (
                    <Button danger onClick={closeTicket}>
                      Закрыть обращение
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal>
      </Card>

      {/* AI Assistant Drawer */}
      <Drawer
        title="ИИ Помощник"
        placement="right"
        onClose={() => setAiDrawerVisible(false)}
        open={aiDrawerVisible}
        width={400}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setAiDrawerVisible(false)}>Закрыть</Button>
            <Button
              type="primary"
              onClick={() => {
                setReply(aiSuggestion);
                setAiDrawerVisible(false);
                message.success('Ответ скопирован в поле ввода');
              }}
            >
              Использовать ответ
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>Предложенный ответ:</div>
          <Input.TextArea
            rows={8}
            value={aiSuggestion}
            onChange={(e) => setAiSuggestion(e.target.value)}
            placeholder="ИИ ассистент генерирует ответы здесь"
          />
        </div>
      </Drawer>
    </>
  );
};

export default SupportAdmin;

